import DeleteIcon from '@mui/icons-material/Delete'
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle'
import { Skeleton } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useUserRole } from '../../hooks/useUserRole'
import ViewEventModal from './ViewEventModal'

const EventsList = ({
  farm,
  events,
  handleDeleteEvent,
  monitoringWorkflows,
  loadError,
  eventsError,
  noFarmSelected,
}) => {
  const [openModal, setOpenModal] = useState(false)
  const { isAdmin } = useUserRole()

  // The status cell and the delete gate must not disagree about whether an event is past.
  const isPastEvent = event => dayjs(event.date).isBefore(dayjs())

  const renderFallbackCells = () => {
    if (loadError || eventsError) {
      return (
        <TableCell align="center" colSpan={9}>
          <Typography color="error" variant="body2">
            {eventsError
              ? 'No se pudieron cargar los eventos de monitoreo. Recargá la página para reintentar.'
              : 'No se pudieron cargar los datos del establecimiento. Recargá la página para reintentar.'}
          </Typography>
        </TableCell>
      )
    }

    if (noFarmSelected) {
      return (
        <TableCell align="center" colSpan={9}>
          <Typography variant="body2">
            Seleccioná un establecimiento para ver sus eventos de monitoreo.
          </Typography>
        </TableCell>
      )
    }

    if (events && monitoringWorkflows) {
      return (
        <TableCell align="center" colSpan={9}>
          <Typography variant="body2">
            Todavía no hay eventos de monitoreo para este establecimiento.
          </Typography>
        </TableCell>
      )
    }

    return Array(9)
      .fill(0)
      .map((e, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableCell key={`skeleton-cell-${i}`} align="center">
          <Skeleton width="100%" />
        </TableCell>
      ))
  }

  return (
    <Box sx={{ padding: '20px' }}>
      {farm && <ViewEventModal farm={farm} open={openModal} setOpenModal={setOpenModal} />}
      <Box sx={{ width: '100%' }}>
        <Stack spacing={2}>
          <TableContainer component={Paper}>
            <Table aria-label="simple table" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    <Typography variant="p">
                      <b>Fecha</b>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="p">
                      <b>Hora</b>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="p">
                      <b>Técnico asignado</b>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="p">
                      <b>Tipo de monitoreo</b>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="p">
                      <b>Estratos</b>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="p">
                      <b>Sitios de monitoreo</b>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="p">
                      <b>Estado</b>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="p">
                      <b>Actividades completas</b>
                    </Typography>
                  </TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Events resolve from the React Query cache independently of the workflows fetch,
                    so both slices must be present before rendering a row. */}
                {events?.length > 0 && monitoringWorkflows ? (
                  events.map(event => (
                    <TableRow
                      key={event.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="center">{dayjs(event.date).format('DD/MM/YYYY')}</TableCell>
                      <TableCell align="center">{dayjs(event.date).format('HH:mm')}</TableCell>
                      <TableCell align="center">
                        {Array.isArray(event.assignedTo)
                          ? event.assignedTo.join(', ')
                          : event.assignedTo}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {event.monitoringWorkflowIds
                            ?.map(id => monitoringWorkflows.find(w => w.id === id))
                            .filter(Boolean)
                            .map(w => `${w.name} (${w.description})`)
                            .join(' - ')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {event.samplingAreas?.map(s => s.name).join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {event.monitoringSites?.map(s => s.name).join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" component="th" scope="row">
                        {isPastEvent(event) ? (
                          event.totalActivities > 0 &&
                          event.completedActivities >= event.totalActivities ? (
                            <Typography sx={{ color: 'success.main' }} variant="body2">
                              Completado
                            </Typography>
                          ) : (
                            <Typography color="error" variant="body2">
                              Incompleto
                            </Typography>
                          )
                        ) : (
                          <Typography sx={{ color: 'warning.main' }} variant="body2">
                            Pendiente
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {event.completedActivities ?? 0}/{event.totalActivities ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip arrow title="Ver información">
                          <Button
                            aria-label="Ver información del establecimiento"
                            sx={{ margin: '5px', minWidth: 0 }}
                            variant="contained"
                            onClick={() => setOpenModal(true)}
                          >
                            <PersonPinCircleIcon sx={{ fontSize: 20 }} />
                          </Button>
                        </Tooltip>

                        <Tooltip arrow title="Eliminar evento">
                          <span>
                            <Button
                              aria-label="Eliminar evento"
                              color="error"
                              disabled={isAdmin ? false : isPastEvent(event)}
                              sx={{ margin: '5px', minWidth: 0 }}
                              variant="outlined"
                              onClick={() => {
                                handleDeleteEvent(event.id)
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 20 }} />
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {renderFallbackCells()}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Box>
    </Box>
  )
}

export default EventsList
